import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';
import type {
  AutowriteSession,
  Candidate,
  Discipline,
  OutlineData,
  PricingInfo,
  Section,
  SessionInfo,
  WizardStep,
  AssembleResult,
} from '../types/autowrite';

interface AutowriteState {
  // Справочники
  disciplines: Discipline[];
  pricing: PricingInfo | null;

  // Текущая сессия
  session: AutowriteSession | null;
  sessionInfo: SessionInfo | null;
  sessions: AutowriteSession[];

  // Оглавление
  outline: OutlineData | null;
  outlineConfirmed: boolean;

  // Разделы и кандидаты
  sections: Section[];
  candidatesMap: Record<string, Candidate[]>; // sectionId -> candidates
  activeSectionId: string | null;

  // Сборка
  assembleResult: AssembleResult | null;

  // UI
  wizardStep: WizardStep;
  isLoading: boolean;
  loadingMessage: string;
  error: string | null;
}

const initialState: AutowriteState = {
  disciplines: [],
  pricing: null,
  session: null,
  sessionInfo: null,
  sessions: [],
  outline: null,
  outlineConfirmed: false,
  sections: [],
  candidatesMap: {},
  activeSectionId: null,
  assembleResult: null,
  wizardStep: 'create',
  isLoading: false,
  loadingMessage: '',
  error: null,
};

const autowriteSlice = createSlice({
  name: 'autowrite',
  initialState,
  reducers: {
    setDisciplines(state, action: PayloadAction<Discipline[]>) {
      state.disciplines = action.payload;
    },
    setPricing(state, action: PayloadAction<PricingInfo>) {
      state.pricing = action.payload;
    },
    setSession(state, action: PayloadAction<AutowriteSession | null>) {
      state.session = action.payload;
      if (action.payload?.sections) {
        state.sections = action.payload.sections;
      }
    },
    setSessionInfo(state, action: PayloadAction<SessionInfo | null>) {
      state.sessionInfo = action.payload;
    },
    setSessions(state, action: PayloadAction<AutowriteSession[]>) {
      state.sessions = action.payload;
    },
    setOutline(state, action: PayloadAction<OutlineData | null>) {
      state.outline = action.payload;
      state.outlineConfirmed = false;
    },
    setOutlineConfirmed(state, action: PayloadAction<boolean>) {
      state.outlineConfirmed = action.payload;
    },
    setSections(state, action: PayloadAction<Section[]>) {
      state.sections = action.payload;
    },
    setCandidates(state, action: PayloadAction<{ sectionId: string; candidates: Candidate[] }>) {
      state.candidatesMap[action.payload.sectionId] = action.payload.candidates;
    },
    setActiveSectionId(state, action: PayloadAction<string | null>) {
      state.activeSectionId = action.payload;
    },
    selectCandidateForSection(state, action: PayloadAction<{ sectionId: string; candidateId: string }>) {
      const section = state.sections.find((s) => s.id === action.payload.sectionId);
      if (section) {
        section.selectedCandidateId = action.payload.candidateId;
        section.status = 'selected';
      }
    },
    setAssembleResult(state, action: PayloadAction<AssembleResult | null>) {
      state.assembleResult = action.payload;
    },
    setWizardStep(state, action: PayloadAction<WizardStep>) {
      state.wizardStep = action.payload;
    },
    setLoading(state, action: PayloadAction<{ loading: boolean; message?: string }>) {
      state.isLoading = action.payload.loading;
      state.loadingMessage = action.payload.message || '';
      if (action.payload.loading) {
        state.error = null;
      }
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    resetAutowrite(state) {
      Object.assign(state, {
        ...initialState,
        disciplines: state.disciplines,
        pricing: state.pricing,
        sessions: state.sessions,
      });
    },
  },
});

export const {
  setDisciplines,
  setPricing,
  setSession,
  setSessionInfo,
  setSessions,
  setOutline,
  setOutlineConfirmed,
  setSections,
  setCandidates,
  setActiveSectionId,
  selectCandidateForSection,
  setAssembleResult,
  setWizardStep,
  setLoading,
  setError,
  resetAutowrite,
} = autowriteSlice.actions;

// Selectors
export const selectAutowrite = (state: RootState) => state.autowrite;
export const selectAutowriteSession = (state: RootState) => state.autowrite.session;
export const selectAutowriteSessionInfo = (state: RootState) => state.autowrite.sessionInfo;
export const selectAutowriteOutline = (state: RootState) => state.autowrite.outline;
export const selectAutowriteSections = (state: RootState) => state.autowrite.sections;
export const selectAutowriteCandidatesMap = (state: RootState) => state.autowrite.candidatesMap;
export const selectAutowriteWizardStep = (state: RootState) => state.autowrite.wizardStep;
export const selectAutowriteIsLoading = (state: RootState) => state.autowrite.isLoading;
export const selectAutowriteError = (state: RootState) => state.autowrite.error;

export default autowriteSlice.reducer;
